import React, { useState, useMemo, useRef, useEffect } from 'react';
import { Project } from '../types/Project';
import { ProjectCard } from '../components/ProjectCard';
import projectsData from '../data/projects.json';

interface ProjectsData {
  projects: Project[];
  certificates: [];
  courses: [];
}

export const ProjectsPage: React.FC = () => {
  const [selectedType, setSelectedType] = useState<string>('all');
  const [techQuery, setTechQuery] = useState<string>('');
  const [showSuggestions, setShowSuggestions] = useState<boolean>(false);
  const suggestionsRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const suggestionsContainerRef = useRef<HTMLDivElement | null>(null);
  
  const allProjects: Project[] = (projectsData as ProjectsData).projects;
  
  // Get unique technologies from all projects
  const technologies = useMemo(() => {
    const techs = new Set<string>();
    allProjects.forEach(project =>
      project.technologies.forEach(tech => techs.add(tech))
    );
    return Array.from(techs).sort();
  }, [allProjects]);

  // Close suggestions when clicking outside
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(e.target as Node)
      ) {
        setShowSuggestions(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close suggestions on window resize to avoid layout issues on narrow viewports
  useEffect(() => {
    // keep suggestions hidden when resizing by default, but we'll also
    // reposition when suggestions are open (see below)
    function handleResize() {
      // if suggestions are visible, reposition them; otherwise keep closed
      if (showSuggestions) {
        positionSuggestions();
      } else {
        setShowSuggestions(false);
      }
    }
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showSuggestions]);

  // Position suggestions container to match the input width/left within the filter-group
  function positionSuggestions() {
    const input = inputRef.current;
    const container = suggestionsContainerRef.current;
    const group = suggestionsRef.current;
    if (!input || !container || !group) return;

    // Ensure container is positioned relative to the filter group
    const inputRect = input.getBoundingClientRect();
    const groupRect = group.getBoundingClientRect();

    // Compute left relative to group
    const left = inputRect.left - groupRect.left;
    const width = Math.min(inputRect.width, Math.max(0, groupRect.width));

    container.style.left = `${Math.max(0, left)}px`;
    container.style.width = `${width}px`;
  }

  // When suggestions open, position them and keep them updated on scroll/resize
  useEffect(() => {
    if (!showSuggestions) return;

    // initial positioning
    positionSuggestions();

    // reposition on scroll and resize while open
    function handleScroll() {
      positionSuggestions();
    }
    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleScroll);
    };
  }, [showSuggestions]);

  // Filter projects based on selected filters
  const filteredProjects = useMemo(() => {
    return allProjects.filter(project => {
      const typeMatch = selectedType === 'all' || project.type === selectedType;
      // If techQuery is empty, match all; otherwise match any technology that includes the query (case-insensitive)
      const techMatch = !techQuery || project.technologies.some(t =>
        t.toLowerCase().includes(techQuery.toLowerCase())
      );
      return typeMatch && techMatch;
    });
  }, [allProjects, selectedType, techQuery]);

  return (
    <div className="projects-page">
      <h1>Portfolio</h1>
      
      <div className="filters">
        <div className="filter-group">
          <label>Type:</label>
          <select 
            value={selectedType} 
            onChange={(e) => setSelectedType(e.target.value)}
          >
            <option value="all">All</option>
            <option value="project">Projects</option>
            <option value="certificate">Certificates</option>
            <option value="course">Courses</option>
          </select>
        </div>

        <div className="filter-group" ref={suggestionsRef}>
          <label htmlFor="tech-search">Technology:</label>
          <input
            id="tech-search"
            ref={inputRef}
            type="search"
            value={techQuery}
            onChange={(e) => { setTechQuery(e.target.value); setShowSuggestions(true); }}
            onFocus={() => setShowSuggestions(true)}
            aria-expanded={showSuggestions}
            placeholder="Search technology..."
            aria-label="Search projects by technology"
            autoComplete="off"
          />

          {showSuggestions && (
            <div ref={suggestionsContainerRef} className="tech-suggestions" role="listbox">
              <button
                type="button"
                className="suggestion-item"
                onClick={() => { setTechQuery(''); setShowSuggestions(false); }}
              >
                All
              </button>

              {technologies
                .filter(t => t.toLowerCase().includes(techQuery.toLowerCase()))
                .map(tech => (
                  <button
                    key={tech}
                    type="button"
                    className="suggestion-item"
                    onClick={() => { setTechQuery(tech); setShowSuggestions(false); }}
                  >
                    {tech}
                  </button>
                ))}
            </div>
          )}
        </div>
      </div>

      <div className="projects-grid">
        {filteredProjects.map(project => (
          <ProjectCard key={project.id} project={project} />
        ))}
      </div>
    </div>
  );
};